/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import * as Sentry from "@sentry/react";
import queryString from "query-string";
import { useRouteMatch } from "react-router";
import { API, graphqlOperation } from "aws-amplify";
import { useHistory } from "react-router-dom";
import { pushReaction } from "../../../common-definitions/reaction";
import { pushActiveProduct } from "../../../common-definitions/product";
import LiveStreamView from "../../templates/LiveStreamView";
import LocalStorage, {
  LocalStorageKey,
} from "../../../local-storage/local-storage";
import { Program, CreateProgramInput, Product } from "../../../API";
import {
  FetchProgram,
  FetchProgramProducts,
  FETCH_PROGRAM_FAILURE,
  FETCH_PROGRAM_PRODUCTS_SUCCESS,
  FETCH_PROGRAM_SUCCESS,
  ProgramContext,
  CreateReaction,
  FetchComments,
  FETCH_COMMENTS_SUCCESS,
  UpdateProgramProductRealtime,
  GoProductDetailExternal,
  FetchProgramCartInByCustomer,
  FETCH_PROGRAM_CARTIN_BY_CUSTOMER_SUCCESS,
  AddProgramCartInRealtime,
  DeleteProgramCartInRealtime,
  UpdateProductRealtime,
  CreateProgramPlayVideo,
} from "../../../context/program";
import ROUTES, { ViewPage } from "../../../common-definitions/routes";
import {
  onCreateProgram,
  onUpdateProgram,
  onModifyProgramByDatatype,
  onUpdateCustomer,
  onDeleteProgram,
  onUpdateProduct,
} from "../../../graphql/subscriptions";
import {
  RecordStatus,
  StoragedManageStatus,
} from "../../../common-definitions/common-enum";
import { getImage } from "../../../common-definitions/storage";
import Loading from "../../organisms/Loading";
import { getUniqueID } from "../../../common-definitions/agora-token-generation";

interface ImageStorage {
  fileName: string;
  fileUrl: string;
}

interface LiveStreamState {
  programProductList: Program[] | null;
  fetchedCartInList: boolean;
  imageUrls: ImageStorage[];
}

const LiveStreamViewPage = () => {
  const history = useHistory();
  const search =
    queryString.parse(history?.location?.search || "") || ({} as any); // eslint-disable-line
  const url = useRouteMatch<ViewPage>();
  const { id } = url.params;
  const { ProgramState, ProgramDispatch } = React.useContext(ProgramContext);
  const [state, setState] = React.useState<LiveStreamState>({
    programProductList: null,
    fetchedCartInList: false,
    imageUrls: [],
  });
  const [loadingProgram, setLoadingProgram] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);
  const [loadingProgramProducts, setLoadingProgramProducts] =
    React.useState(false);

  const fetchProgram = () => {
    ProgramDispatch(FetchProgram(id));
  };

  const isFinishedContractMode =
    search.contractStatus && search.contractStatus === "finished";

  React.useEffect(() => {
    fetchProgram();
    ProgramDispatch(FetchComments(id));
    ProgramDispatch(FetchProgramProducts(id));
    getUniqueID();
  }, []);

  const pushProductActive = async (product: Program) => {
    if (product?.product?.productImageUrl) {
      const image = (await getImage(
        product.product?.productImageUrl,
        "product"
      )) as string;
      pushActiveProduct(product.dataType, image);
    } else {
      pushActiveProduct(product.dataType);
    }
  };

  React.useEffect(() => {
    // Realtime updateProgram
    const updateProgram = (
      API.graphql(
        graphqlOperation(onModifyProgramByDatatype, {
          id,
          dataType: "program",
        })
      ) as any
    ).subscribe({
      next: (data: any) => {
        const updateData = data.value.data.onModifyProgramByDatatype as Program;
        if (updateData.dataType === "program") {
          ProgramDispatch(FetchProgram(id));
          ProgramDispatch(FetchProgramProducts(id));
        }
      },
    });

    // Realtime reaction
    const createReaction = (
      API.graphql(graphqlOperation(onCreateProgram)) as any
    ).subscribe({
      next: (data: any) => {
        const newData = data.value.data.onCreateProgram as Program;
        if (
          (newData.dataType.substring(0, 9) === "reaction#" &&
            newData?.userId !== LocalStorage.get(LocalStorageKey.userId)) ||
          (newData.dataType.substring(0, 7) === "cartIn#" &&
            ![
              LocalStorage.get(LocalStorageKey.userId),
              LocalStorage.get(LocalStorageKey.userName),
            ].includes(newData?.userId || ""))
        ) {
          pushReaction(false);
        }
      },
    });

    // Realtime customer update
    const updateCustomer = (
      API.graphql(graphqlOperation(onUpdateCustomer)) as any
    ).subscribe({
      next: (data: any) => {
        const updateData = data.value.data.onUpdateCustomer as Program;
        if (updateData.dataType === "customer") {
          ProgramDispatch(FetchProgram(id));
        }
      },
    });

    // Realtime update program product
    const updateProduct = (
      API.graphql(graphqlOperation(onUpdateProgram)) as any
    ).subscribe({
      next: (data: any) => {
        const updateData = data.value.data.onUpdateProgram as Program;
        // Realtime update program products
        if (
          updateData.id === id &&
          updateData.dataType.substring(0, 8) === "product#"
        ) {
          ProgramDispatch(UpdateProgramProductRealtime(updateData));
          // Check add new product
          const domProduct = document.getElementById(updateData?.dataType);
          if (
            updateData?.productStatusUpdateAt?.substring(0, 2) === "1#" &&
            !domProduct
          ) {
            pushProductActive(updateData);
          }
        }
      },
    });

    return () => {
      updateProgram.unsubscribe();
      createReaction.unsubscribe();
      updateCustomer.unsubscribe();
      updateProduct.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    // Realtime update product info
    const updateProductInfo = (
      API.graphql(graphqlOperation(onUpdateProduct)) as any
    ).subscribe({
      next: (data: any) => {
        const updateData = data.value.data.onUpdateProduct as Product;
        // Realtime update product info
        if (updateData.customerId === ProgramState?.program?.customerId) {
          ProgramDispatch(UpdateProductRealtime(updateData));
        }
      },
      error: (error: any) => {
        // eslint-disable-next-line
        console.warn(error);
      },
    });

    const addCartIn = (
      API.graphql(graphqlOperation(onCreateProgram)) as any
    ).subscribe({
      next: (data: any) => {
        const newData = data.value.data.onCreateProgram;
        if (
          newData.dataType.substring(0, 7) === "cartIn#" &&
          newData.customerId === ProgramState?.program?.customerId
        ) {
          ProgramDispatch(AddProgramCartInRealtime(newData));
        }
      },
      // eslint-disable-next-line
      error: (error: any) => {
        // eslint-disable-next-line
        console.warn(error);
      },
    });

    const deleteCartIn = (
      API.graphql(graphqlOperation(onDeleteProgram)) as any
    ).subscribe({
      // eslint-disable-next-line
      next: (data: any) => {
        const deleteData = data.value.data.onDeleteProgram;
        if (
          deleteData.dataType.substring(0, 7) === "cartIn#" &&
          deleteData.customerId === ProgramState?.program?.customerId
        ) {
          ProgramDispatch(DeleteProgramCartInRealtime(deleteData.dataType));
        }
      },
      // eslint-disable-next-line
      error: (error: any) => {
        // eslint-disable-next-line
        console.warn(error);
      },
    });

    return () => {
      updateProductInfo.unsubscribe();
      addCartIn.unsubscribe();
      deleteCartIn.unsubscribe();
    };
  }, [ProgramState?.program?.customerId]);

  const mapProductImage = async (productList: Program[]) => {
    const imageUrls = [...state.imageUrls];
    const programProductList = await Promise.all(
      productList.map(async (product: Program) => {
        if (
          product?.product?.productImageUrl &&
          product?.product?.productImageUrl.substring(0, 4) !== "http"
        ) {
          let image;
          const imageExist = (state.imageUrls || []).find(
            (img: ImageStorage) =>
              img?.fileName === product?.product?.productImageUrl
          );
          if (imageExist) {
            image = imageExist.fileUrl;
          } else {
            image = (await getImage(
              product.product?.productImageUrl,
              "product"
            )) as string;
            imageUrls.push({
              fileName: product.product?.productImageUrl,
              fileUrl: image,
            });
          }
          return {
            ...product,
            product: {
              ...product.product,
              productImageUrl: image,
            },
          };
        }
        return product;
      })
    );
    setState({
      ...state,
      programProductList,
      imageUrls,
    });
    setLoadingProgramProducts(true);
  };

  const handleCreateReaction = (isRehearsal: 0 | 1) => {
    const now = Date.now();
    ProgramDispatch(
      CreateReaction({
        id,
        dataType: `reaction#${now}`,
        reactionDatetime: `${now}`,
        customerId: ProgramState?.program?.customerId || "",
        userId: LocalStorage.get(LocalStorageKey.userId) || "",
        isRehearsal: isRehearsal || 0,
      })
    );
  };

  const handleGoProductDetailExternal = (data: CreateProgramInput) => {
    ProgramDispatch(GoProductDetailExternal(data));
  };

  React.useEffect(() => {
    switch (ProgramState.result) {
      case FETCH_PROGRAM_SUCCESS:
        if (
          !ProgramState.program ||
          ProgramState.program.isDeleted === RecordStatus.DELETED
        ) {
          let error = `Program ${id} is null`;
          if (ProgramState?.program?.isDeleted === RecordStatus.DELETED) {
            error = `Program ${id} is deleted`;
          }
          Sentry.captureException({
            message: error,
            data: JSON.stringify(ProgramState.program),
          });
          history.replace(ROUTES.ERROR_404);
        }
        if (ProgramState.program) {
          setLoadingProgram(true);
        }
        ProgramDispatch(
          FetchProgramCartInByCustomer(ProgramState.program?.customerId || "")
        );
        break;
      case FETCH_PROGRAM_FAILURE:
        history.replace(ROUTES.ERROR_404);
        break;
      case FETCH_PROGRAM_PRODUCTS_SUCCESS:
        setLoadingProgramProducts(true);
        break;
      case FETCH_COMMENTS_SUCCESS:
        setLoadingComments(true);
        break;
      case FETCH_PROGRAM_CARTIN_BY_CUSTOMER_SUCCESS:
        setState((s) => ({
          ...s,
          fetchedCartInList: true,
        }));
        break;
      default:
        break;
    }
  }, [ProgramState.result, ProgramState.program]);

  React.useEffect(() => {
    mapProductImage(ProgramState?.programProducts || []);
  }, [ProgramState?.programProducts]);

  React.useEffect(() => {
    if (
      ProgramState.program?.customer?.items &&
      ProgramState.program?.customer.items.length > 0
    ) {
      ProgramState.program?.customer.items.forEach((item) => {
        if (item?.contractStatus === "終了" && !isFinishedContractMode) {
          history.replace(ROUTES.ERROR_404);
        }
      });
    }
  }, [ProgramState.program]);

  const isShowProductStatus = React.useMemo(() => {
    if (ProgramState.program?.storagedManage === StoragedManageStatus.OFF) {
      return false;
    }
    if (ProgramState.program?.storagedManage === StoragedManageStatus.AUTO) {
      return state.fetchedCartInList;
    }
    return true;
  }, [ProgramState.program?.storagedManage, state.fetchedCartInList]);

  const onPlayVideoArchived = () => {
    const now = Date.now();
    ProgramDispatch(
      CreateProgramPlayVideo({
        id,
        dataType: `playVideo#${now}`,
        archivedVideoPlayTime: `${now}`,
        customerId: ProgramState?.program?.customerId || "",
        userId: LocalStorage.get(LocalStorageKey.userId) || "",
      })
    );
  };

  React.useEffect(() => {
    if (ProgramState.program?.programName) {
      document.title = `${ProgramState.program?.programName} | 自社でライブコマースできるくん`;
    }
    let clBackground = "";
    let clTextPrimary = "";
    let clTextSecondary = "";
    let clTextNega = "";
    let clBoxBackground = "";
    let clPoint = "";
    let clLink = "";
    let clMovieBar = "";
    let clMovieBg = "";
    let clDivider = "";
    let clSkeleton = "";
    let classAdd = "";
    const classRemove = [] as string[];
    if (ProgramState.program?.theme) {
      switch (ProgramState.program.theme) {
        case "dark":
          clBackground = "#161616";
          clTextPrimary = "#fff";
          clTextSecondary = "rgba(255, 255, 255, 0.8)";
          clTextNega = "#fff";
          clBoxBackground = "#000";
          clPoint = "#FAFF05";
          clLink = "#58FFD7";
          clMovieBar = "#262626";
          clMovieBg = "#323232";
          clDivider = "rgba(255, 255, 255, 0.3)";
          clSkeleton = "#2E2E2E";
          classAdd = "is-darkmode";
          classRemove.push("is-orangemode");
          classRemove.push("is-bluemode");
          break;
        case "orange":
          clBackground = "#E9DED7";
          clTextPrimary = "#09194F";
          clTextSecondary = "rgba(9, 25, 79, 0.8)";
          clTextNega = "#fff";
          clBoxBackground = "rgba(243, 243, 243, 0.9)";
          clPoint = "#E27F32";
          clLink = "#C84A2B";
          clMovieBar = "#262626";
          clMovieBg = "#323232";
          clDivider = "rgba(163, 147, 145, 0.3)";
          clSkeleton = "#F5F5F5";
          classAdd = "is-orangemode";
          classRemove.push("is-darkmode");
          classRemove.push("is-bluemode");
          break;
        case "blue":
          clBackground = "#DBE3EC";
          clTextPrimary = "#0D194C";
          clTextSecondary = "rgba(6, 16, 52, 0.8)";
          clTextNega = "#fff";
          clBoxBackground = "#F0EFEF";
          clPoint = "#DE3123";
          clLink = "#0D194C";
          clMovieBar = "#262626";
          clMovieBg = "#323232";
          clDivider = "rgba(163, 147, 145, 0.3)";
          clSkeleton = "#F5F5F5";
          classAdd = "is-bluemode";
          classRemove.push("is-darkmode");
          classRemove.push("is-orangemode");
          break;
        default:
          break;
      }
    }
    const doc = document.documentElement;
    if (classRemove.length) {
      classRemove.forEach((clRemove: string) => {
        document.body.classList.remove(`${clRemove}`);
      });
    }
    if (classAdd !== "") {
      document.body.classList.add(`${classAdd}`);
    }
    doc.style.setProperty("--cl_background", `${clBackground}`);
    doc.style.setProperty("--cl_text_primary", `${clTextPrimary}`);
    doc.style.setProperty("--cl_text_secondary", `${clTextSecondary}`);
    doc.style.setProperty("--cl_text_nega", `${clTextNega}`);
    doc.style.setProperty("--cl_box_background", `${clBoxBackground}`);
    doc.style.setProperty("--cl_point", `${clPoint}`);
    doc.style.setProperty("--cl_link", `${clLink}`);
    doc.style.setProperty("--cl_movie_bar", `${clMovieBar}`);
    doc.style.setProperty("--cl_movie_bg", `${clMovieBg}`);
    doc.style.setProperty("--cl_divider", `${clDivider}`);
    doc.style.setProperty("--cl_skeleton", `${clSkeleton}`);
  }, [ProgramState.program]);

  if (!loadingProgram || !loadingComments || !loadingProgramProducts) {
    return <Loading />;
  }

  return (
    <LiveStreamView
      programProducts={state.programProductList}
      program={ProgramState.program || ({} as Program)}
      createReaction={handleCreateReaction}
      handleGoProductDetailExternal={handleGoProductDetailExternal}
      fetched={loadingProgram && loadingComments && loadingProgramProducts}
      cartInList={ProgramState.cartinProgramByCustomer || []}
      isShowStatusProduct={isShowProductStatus}
      onPlayVideoArchived={onPlayVideoArchived}
    />
  );
};

export default LiveStreamViewPage;
/* eslint-enable @typescript-eslint/no-explicit-any */
